import { type HTMLProps, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaXmark } from 'react-icons/fa6';
import { VscDebugRestart } from 'react-icons/vsc';
import {
  Button,
  clsxMerge,
  TextField,
  Typography,
} from '@breezeai-frontend/cargo-ui';
import { type FormApi } from '@tanstack/react-form';
import { type zodValidator } from '@tanstack/zod-form-adapter';

import { useAppConfiguration } from '../../../context/app-configuration/useAppConfiguration';
import { useFeatureToggle } from '../../../context/auth/auth-hooks';
import { usePlatform } from '../../../context/PlatformContext';
import { type ConveyanceType } from '../../../model/Shipment';
import {
  type Location,
  type PlaceLocation,
} from '../../../network/apis/locations/types';
import { useFormLogic } from '../../certificates/CertificateFlowPage/useFormLogic';
import { ModeOfTransportSelect } from './ModeOfTransportSelect';
import { PdfTooltip } from './PdfTooltip';
import {
  type ComboBoxKey,
  PortsAndPlacesComboBox,
} from './PortsAndPlacesComboBox';
import { type RouteDetailsFormData } from './types';
import { useRouteDetailsLogic } from './useRouteDetailsLogic';
import { getPlaceholderText, getSelectionIcon } from './utils';

type EventListeners = {
  primaryMot: {
    onChange?: (value: { name: ConveyanceType; displayName: string }) => void;
  };
};

export interface RouteDetailsProps {
  form: FormApi<RouteDetailsFormData, typeof zodValidator>;
  listeners?: Partial<EventListeners>;
  isUpdate?: boolean;
  isDuplicate?: boolean;
  isDisabled?: boolean;
}

const Dash = ({ className, ...props }: HTMLProps<HTMLDivElement>) => (
  <div
    className={clsxMerge(
      `border-l-2 border-dashed border-l-field-border-default h-10 ml-4`,
      className,
    )}
    {...props}
  />
);

export function RouteDetails({
  form,
  listeners,
  isUpdate,
  isDuplicate,
  isDisabled = false,
}: RouteDetailsProps) {
  const { locationHandleBlur } = useFormLogic();
  const { platform, isWtw } = usePlatform();
  const { t } = useTranslation();

  const isPdfEditingEnabled = useFeatureToggle('enable-pdf-editing');

  const {
    primaryMot,
    showSecondaryTransport,
    setShowSecondaryTransport,
    showPlaceOfLoading,
    setShowPlaceOfLoading,
    showPlaceOfDelivery,
    setShowPlaceOfDelivery,
    originInputValue,
    destinationInputValue,
    placeOfLoadingInputValue,
    placeOfDeliveryInputValue,
    setOriginInputValue,
    setDestinationInputValue,
    setPlaceOfLoadingInputValue,
    setPlaceOfDeliveryInputValue,
    resetRouteDetails,
    handleCloseSecondary,
    handleCloseSecondaries,
    isRouteDetailsDirty,
  } = useRouteDetailsLogic(form);

  const { modes_of_transport, secondaries_modes_of_transport } =
    useAppConfiguration();

  useEffect(() => {
    if (primaryMot?.name === 'sea' || primaryMot?.name === 'air') {
      setShowSecondaryTransport(true);
    } else {
      handleCloseSecondaries();
    }
  }, [primaryMot, handleCloseSecondaries, setShowSecondaryTransport]);

  return (
    <div data-testid="route-details" className="w-full">
      <div className="flex flex-row justify-between items-center pb-5 w-full">
        <Typography level="h3">
          {t('routeDetails.Title', {
            ns: 'common',
            context: platform,
          })}
        </Typography>
        {isRouteDetailsDirty && (
          <Button
            data-testid="reset-route-details-button"
            leftIcon={<VscDebugRestart className="size-4" />}
            variant="ghost"
            customStyles="px-2 gap-1"
            onPress={resetRouteDetails}
            label={t('routeDetails.ResetRouteDetails', {
              ns: 'common',
              context: platform,
            })}
          />
        )}
      </div>
      <div className="flex flex-col">
        {showSecondaryTransport && !showPlaceOfLoading && (
          <div className="relative">
            <Button
              data-testid="add-place-of-loading-button"
              variant="secondary"
              width="square"
              onPress={() => setShowPlaceOfLoading(true)}
              customStyles="w-fit"
              isDisabled={isDisabled}
            >
              <div className="flex items-center p-2 gap-1">
                <FaPlus />{' '}
                <span>
                  {t('routeDetails.CoverDoorToPortAddLoadingPlace', {
                    ns: 'common',
                    context: platform,
                  })}
                </span>
              </div>
            </Button>
            <Dash />
          </div>
        )}
        <div
          data-testid="place-of-loading-section"
          className={clsxMerge(
            (!showSecondaryTransport || !showPlaceOfLoading) && 'hidden',
          )}
        >
          <div className="grid grid-cols-[1fr_auto] items-center">
            <form.Field
              validators={{
                onSubmit: ({ value }) => {
                  if (!value && showPlaceOfLoading) {
                    return 'Please select a place of loading';
                  }
                  return;
                },
              }}
              name="placeOfLoading"
              children={(field) => {
                const value = field.getValue();
                const mot = field.form.getFieldValue('placeOfLoadingMot');

                const SelectionIcon = value
                  ? getSelectionIcon(mot?.name)
                  : undefined;

                return (
                  <PortsAndPlacesComboBox
                    isDisabled={isDisabled}
                    data-testid="place-of-loading-combobox"
                    label="Place of loading"
                    validationBehavior="aria"
                    id={field.name}
                    name={field.name}
                    onBlur={(e) =>
                      locationHandleBlur(e, field, setPlaceOfLoadingInputValue)
                    }
                    filterByMot={mot?.name ?? undefined}
                    inputValue={placeOfLoadingInputValue ?? ''}
                    onInputChange={(value) => {
                      setPlaceOfLoadingInputValue(value);
                      return;
                    }}
                    onSelectionChange={(key: ComboBoxKey) => {
                      if (key) {
                        const location: PlaceLocation = JSON.parse(String(key));
                        field.handleChange(location);

                        const roadMot = secondaries_modes_of_transport.find(
                          (mot) => mot.name === 'road',
                        );

                        field.form.setFieldValue('placeOfLoadingMot', roadMot);
                        const meta =
                          field.form.getFieldMeta('placeOfLoadingMot');
                        if (!meta) return;
                        field.form.setFieldMeta('placeOfLoadingMot', {
                          ...meta,
                          isDirty: true,
                        });
                      }
                    }}
                    SelectionIcon={SelectionIcon}
                    offsetErrorMessage={2}
                    isInvalid={field.state.meta.errors.length > 0}
                    errorMessage={field.state.meta.errors[0] || undefined}
                  />
                );
              }}
            />
            {!isDisabled && (
              <Button
                variant="ghost"
                onPress={() => handleCloseSecondary('loading')}
                data-testid="close-place-of-loading-button"
              >
                <FaXmark className="text-system-grey-600 " />
              </Button>
            )}
            {isWtw &&
              form.state.values.placeOfLoading &&
              !isPdfEditingEnabled && (
                <form.Field
                  name="placeOfLoadingPdfOverride"
                  children={(field) => (
                    <TextField
                      aria-label="Text to appear on certificate"
                      data-testid="place-of-loading-pdf-override-field"
                      placeholder={t(
                        'routeDetails.PdfOverrideFieldPlaceholder',
                        {
                          ns: 'common',
                          context: platform,
                        },
                      )}
                      icon={<PdfTooltip />}
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={field.handleChange}
                      isInvalid={field.state.meta.errors?.length > 0}
                      errorMessage={field.state.meta.errors.join(', ')}
                      customStyles="min-w-70 mt-1 row-[2]"
                    />
                  )}
                />
              )}
          </div>
          <Dash />
          <form.Field
            name="placeOfLoadingMot"
            children={(field) => (
              <ModeOfTransportSelect
                isDisabled={isDisabled}
                data-testid="loading-transport-mode-select"
                id={field.name}
                items={secondaries_modes_of_transport}
                name={field.name}
                selectedItem={field.getValue()}
                onSelectionChange={(item) => {
                  field.handleChange(item);
                  const placeOfLoading =
                    field.form.getFieldValue('placeOfLoading');
                  if (placeOfLoading) {
                    // Not sure how else to trigger a re-render of the field
                    field.form.setFieldValue('placeOfLoading', {
                      ...placeOfLoading,
                    });
                  }
                }}
              />
            )}
          />
          <Dash />
        </div>
        <div className={clsxMerge('relative', !isDisabled && 'pr-11')}>
          <form.Field
            name="origin"
            validators={{
              onChange: ({ fieldApi }) => {
                // Re-run existing validators when value changes
                fieldApi.validate('blur');
                return undefined;
              },
              onBlur: ({ value }) => {
                if (!value) {
                  return 'Please select an origin';
                }
                return;
              },
            }}
            children={(field) => {
              const value = field.getValue();
              const mot = field.form.getFieldValue('primaryMot');

              const SelectionIcon =
                value && mot ? getSelectionIcon(mot.name) : undefined;

              return (
                <PortsAndPlacesComboBox
                  isDisabled={isDisabled}
                  data-testid="origin-combobox"
                  label={t('routeDetails.Origin', {
                    ns: 'common',
                    context: platform,
                  })}
                  validationBehavior="aria"
                  placeholder={mot && getPlaceholderText(mot.name)}
                  isRequired
                  id={field.name}
                  name={field.name}
                  onBlur={(e) =>
                    locationHandleBlur(e, field, setOriginInputValue)
                  }
                  inputValue={originInputValue}
                  onInputChange={(value) => {
                    setOriginInputValue(value);
                    return;
                  }}
                  onSelectionChange={(key: ComboBoxKey) => {
                    if (key) {
                      const location: Location = JSON.parse(String(key));
                      field.handleChange(location);
                    }

                    return;
                  }}
                  SelectionIcon={SelectionIcon}
                  offsetErrorMessage={2}
                  isInvalid={field.state.meta.errors.length > 0}
                  errorMessage={field.state.meta.errors.join(', ')}
                  filterByMot={mot?.name}
                />
              );
            }}
          />
          {isWtw && form.state.values.origin && !isPdfEditingEnabled && (
            <form.Field
              name="originPdfOverride"
              children={(field) => (
                <TextField
                  aria-label="Text to appear on certificate"
                  data-testid="origin-pdf-override-field"
                  placeholder={t('routeDetails.PdfOverrideFieldPlaceholder', {
                    ns: 'common',
                    context: platform,
                  })}
                  icon={<PdfTooltip />}
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}
                  isInvalid={field.state.meta.errors?.length > 0}
                  errorMessage={field.state.meta.errors.join(', ')}
                  customStyles="min-w-70 mt-1"
                />
              )}
            />
          )}
          <Dash />
        </div>
        <div className="relative">
          <Typography level="subtext" color="tertiary" htmlFor="primaryMot">
            {t('routeDetails.MainTransportMode', {
              ns: 'common',
              context: platform,
            })}
          </Typography>
          <form.Field
            name="primaryMot"
            validators={{
              onChangeListenTo: ['origin', 'destination'],
              onChange: ({ fieldApi }) => {
                const meta = fieldApi.getMeta();
                fieldApi.setMeta({
                  ...meta,
                  errorMap: { ...meta.errorMap, onBlur: undefined },
                });

                return undefined;
              },
              onBlur: ({ value }) => {
                if (!value) {
                  return 'Please select a mode of transportation';
                }
                return;
              },
            }}
            children={(field) => {
              const isShouldPulse =
                (form.state.isPristine || !field.state.value) &&
                !isUpdate &&
                !isDuplicate &&
                !isDisabled;
              const selectedMot = modes_of_transport.find(
                (mot) =>
                  mot.name === field.getValue()?.name &&
                  mot.display_name === field.getValue()?.display_name,
              );
              return (
                <div
                  className={clsxMerge(
                    isShouldPulse &&
                      'w-fit rounded-lg animate-[shadowpulse_1.5s_infinite_ease-out]',
                  )}
                >
                  <ModeOfTransportSelect
                    isDisabled={isDisabled}
                    items={modes_of_transport}
                    data-testid="primary-transport-mode-select"
                    validationBehavior="aria"
                    isRequired
                    id={field.name}
                    name={field.name}
                    onBlur={field.handleBlur}
                    selectedItem={selectedMot}
                    onSelectionChange={({ name, display_name }) => {
                      field.handleChange({
                        name,
                        display_name,
                      });
                      const fieldsToClear = [
                        'origin',
                        'destination',
                        'placeOfLoading',
                        'placeOfDelivery',
                      ] as const;
                      fieldsToClear.forEach((field) => {
                        form.setFieldValue(field, null);
                        form.setFieldMeta(field, {
                          isTouched: false,
                          errors: [],
                          isPristine: true,
                          errorMap: {},
                          isValidating: false,
                          touchedErrors: [],
                          isDirty: true,
                        });
                      });
                      setOriginInputValue('');
                      setDestinationInputValue('');
                      setPlaceOfLoadingInputValue('');
                      setPlaceOfDeliveryInputValue('');

                      listeners?.primaryMot?.onChange?.({
                        name,
                        displayName: display_name,
                      });

                      const origin = field.form.getFieldValue('origin');
                      if (origin) {
                        // I don't know how else we can trigger a re-render of the
                        // Origin field.
                        field.form.setFieldValue('origin', { ...origin });
                      }
                    }}
                    offsetErrorMessage={true}
                    isInvalid={field.state.meta.errors.length > 0}
                    errorMessage={field.state.meta.errors.join(', ')}
                  />
                </div>
              );
            }}
          />
          <Dash />
        </div>
        <div className={clsxMerge('relative', !isDisabled && 'pr-11')}>
          <form.Field
            name="destination"
            validators={{
              onChange: ({ fieldApi }) => {
                // Re-run existing validators when value changes
                fieldApi.validate('blur');
                return undefined;
              },
              onBlur: ({ value }) => {
                if (!value) {
                  return 'Please select a destination';
                }
                return;
              },
            }}
            children={(field) => {
              const value = field.getValue();
              const mot = field.form.getFieldValue('primaryMot');

              const SelectionIcon =
                value && mot ? getSelectionIcon(mot.name) : undefined;
              return (
                <PortsAndPlacesComboBox
                  isDisabled={isDisabled}
                  filterByMot={mot?.name}
                  data-testid="destination-combobox"
                  label={t('routeDetails.Destination', {
                    ns: 'common',
                    context: platform,
                  })}
                  validationBehavior="aria"
                  isRequired
                  placeholder={mot && getPlaceholderText(mot.name)}
                  id={field.name}
                  name={field.name}
                  onBlur={(e) =>
                    locationHandleBlur(e, field, setDestinationInputValue)
                  }
                  inputValue={destinationInputValue}
                  onInputChange={(value) => {
                    setDestinationInputValue(value);
                    return;
                  }}
                  onSelectionChange={(key: ComboBoxKey) => {
                    if (key) {
                      const location: Location = JSON.parse(String(key));
                      field.handleChange(location);

                      const mot = field.form.getFieldValue('primaryMot');
                      if (location.type === 'port' && !mot) {
                        const primaryMot =
                          location.port.type === 'marine'
                            ? modes_of_transport.find((value) => {
                                return value.name === 'sea';
                              }) ?? { name: 'sea', display_name: 'Sea' }
                            : modes_of_transport.find((value) => {
                                return value.name === 'air';
                              }) ?? { name: 'air', display_name: 'Air' };

                        field.form.setFieldValue('primaryMot', {
                          name: primaryMot.name,
                          display_name: primaryMot.display_name,
                        });
                        listeners?.primaryMot?.onChange?.({
                          name: primaryMot.name,
                          displayName: primaryMot.display_name,
                        });

                        setShowSecondaryTransport(true);
                      }

                      if (location.type === 'place' && !mot) {
                        field.form.setFieldValue('primaryMot', {
                          name: 'road',
                          display_name: 'road',
                        });
                        listeners?.primaryMot?.onChange?.({
                          name: 'road',
                          displayName: 'road',
                        });
                        handleCloseSecondaries();
                      }
                    }

                    return;
                  }}
                  SelectionIcon={SelectionIcon}
                  offsetErrorMessage={2}
                  isInvalid={field.state.meta.errors.length > 0}
                  errorMessage={field.state.meta.errors.join(', ')}
                />
              );
            }}
          />
          {isWtw && form.state.values.destination && !isPdfEditingEnabled && (
            <form.Field
              name="destinationPdfOverride"
              children={(field) => (
                <TextField
                  aria-label="Text to appear on certificate"
                  data-testid="destination-pdf-override-field"
                  placeholder={t('routeDetails.PdfOverrideFieldPlaceholder', {
                    ns: 'common',
                    context: platform,
                  })}
                  icon={<PdfTooltip />}
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}
                  isInvalid={field.state.meta.errors?.length > 0}
                  errorMessage={field.state.meta.errors.join(', ')}
                  customStyles="min-w-70 mt-1"
                />
              )}
            />
          )}
          {showSecondaryTransport && <Dash />}
        </div>
        {showSecondaryTransport && !showPlaceOfDelivery && (
          <Button
            data-testid="add-place-of-delivery-button"
            variant="secondary"
            width="square"
            onPress={() => setShowPlaceOfDelivery(true)}
            customStyles="w-fit"
            isDisabled={isDisabled}
          >
            <div className="flex items-center p-2 gap-1">
              <FaPlus />
              <span>
                {t('routeDetails.CoverPortToDoorAddDeliveryPlace', {
                  ns: 'common',
                  context: platform,
                })}
              </span>
            </div>
          </Button>
        )}
        <div
          data-testid="place-of-delivery-section"
          className={clsxMerge(
            `flex flex-col`,
            (!showSecondaryTransport || !showPlaceOfDelivery) && 'hidden',
          )}
        >
          <div className="relative">
            <form.Field
              name="placeOfDeliveryMot"
              children={(field) => (
                <ModeOfTransportSelect
                  isDisabled={isDisabled}
                  items={secondaries_modes_of_transport}
                  data-testid="delivery-transport-mode-select"
                  id={field.name}
                  name={field.name}
                  selectedItem={field.getValue()}
                  onSelectionChange={(item) => {
                    field.handleChange(item);
                    const placeOfDelivery =
                      field.form.getFieldValue('placeOfDelivery');
                    if (placeOfDelivery) {
                      // Not sure how else to trigger a re-render of the field
                      field.form.setFieldValue('placeOfDelivery', {
                        ...placeOfDelivery,
                      });
                    }
                  }}
                />
              )}
            />
            <Dash />
          </div>
          <div className="grid grid-cols-[1fr_auto] items-center">
            <form.Field
              validators={{
                onSubmit: ({ value }) => {
                  if (!value && showPlaceOfDelivery) {
                    return 'Please select a place of delivery';
                  }
                  return;
                },
              }}
              name="placeOfDelivery"
              children={(field) => {
                const value = field.getValue();
                const mot = field.form.getFieldValue('placeOfDeliveryMot');

                const SelectionIcon = value
                  ? getSelectionIcon(mot?.name)
                  : undefined;
                return (
                  <PortsAndPlacesComboBox
                    isDisabled={isDisabled}
                    data-testid="place-of-delivery-combobox"
                    label="Place of Discharge"
                    validationBehavior="aria"
                    id={field.name}
                    name={field.name}
                    onBlur={(e) =>
                      locationHandleBlur(e, field, setPlaceOfDeliveryInputValue)
                    }
                    filterByMot={mot?.name ?? undefined}
                    inputValue={placeOfDeliveryInputValue ?? ''}
                    onInputChange={(value) => {
                      setPlaceOfDeliveryInputValue(value);
                      return;
                    }}
                    onSelectionChange={(key: ComboBoxKey) => {
                      if (key) {
                        const location: PlaceLocation = JSON.parse(String(key));
                        field.handleChange(location);

                        const roadMot = secondaries_modes_of_transport.find(
                          (mot) => mot.name === 'road',
                        );

                        field.form.setFieldValue('placeOfDeliveryMot', roadMot);
                        const meta =
                          field.form.getFieldMeta('placeOfDeliveryMot');
                        if (!meta) return;
                        field.form.setFieldMeta('placeOfDeliveryMot', {
                          ...meta,
                          isDirty: true,
                        });
                      }

                      return;
                    }}
                    SelectionIcon={SelectionIcon}
                    offsetErrorMessage={2}
                    isInvalid={field.state.meta.errors.length > 0}
                    errorMessage={field.state.meta.errors[0] || undefined}
                  />
                );
              }}
            />
            {!isDisabled && (
              <Button
                variant="ghost"
                onPress={() => handleCloseSecondary('delivery')}
                data-testid="close-place-of-delivery-button"
              >
                <FaXmark className="text-system-grey-600" />
              </Button>
            )}
            {isWtw &&
              form.state.values.placeOfDelivery &&
              !isPdfEditingEnabled && (
                <form.Field
                  name="placeOfDeliveryPdfOverride"
                  children={(field) => (
                    <TextField
                      aria-label="Text to appear on certificate"
                      data-testid="place-of-delivery-pdf-override-field"
                      placeholder={t(
                        'routeDetails.PdfOverrideFieldPlaceholder',
                        {
                          ns: 'common',
                          context: platform,
                        },
                      )}
                      icon={<PdfTooltip />}
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={field.handleChange}
                      isInvalid={field.state.meta.errors?.length > 0}
                      errorMessage={field.state.meta.errors.join(', ')}
                      customStyles="min-w-70 mt-1 row-[2]"
                    />
                  )}
                />
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
